import * as React from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    Chip,
    Button,
    Stack,
    IconButton, alpha,
} from "@mui/material";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import javaRenaissanceBlogImg from "../assets/images/java-duke.jpg";

const gutterX = { xs: 2, sm: 3, md: 6, lg: 16 } as const;


type Article = {
    index: number;
    title: string;
    description: string;
    meta: string;
    tags: string[];
    href: string;
    accent?: "primary" | "secondary" | "success" | "warning";
};

const articles: Article[] = [
    {
        index: 0,
        title: "Embabel or LangGraph ?(And Why Companies failed to replace engineers with AI)",
        description:
            "How Increasing Workload Complexity, Not Smarter Tools, Revealed the Irreplaceable Role of Humans.",
        meta: "Feb 19, 2026",
        tags: [
            "Artificial Intelligence", "Complexity Theory", "Automation", "Decision Theory",
            "Software Engineering", "Socio-Technical Systems", "AI Engineering"
        ],
        href: "/blog/autonomy-architecture",
        accent: "secondary",
    },
    {
        index: 1,
        title: "The Java Renaissance: Why the “Rewrite in Rust” Era is coming to an End",
        description:
            "How Loom, Panama, Valhalla, Leyden and a decade of hard lessons dismantled the real reasons companies abandoned Java.",
        meta: "Jan 6, 2026",
        tags:  ["Java", "Cloud Architecture", "Distributed Systems", "Microservices", "Performance Engineering", "OpenJDK", "Software Infrastructure"
        ]
        ,
        href: "/blog/java-renaissance",
        accent: "secondary",
    },
    {
        index: 2,
        title: "When Information Learned to Think",
        description:
            "Information theory’s journey from West Africa's talking drums(700AD) and telegraphs(1800s) to bits, logic, AI, and the human brain(21st Century)",
        meta: "Jun 20, 2025",
        tags: ["Information Theory", "Shannon Entropy", "Cybernetics", "Codes & Compression", "Robotics",
            "History of Computing", "Philosophy of Information"],
        href: "/blog/information-theory",
        accent: "success",
    },
    ,
];
const articleAccentMap = [
    "#C44A4A",
    "#6B5FD2",
    "#3FA16A",
];

function ArticleCard({ item }: { item: Article }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                height: "85%",
                borderRadius: 3,
                p: { xs: 3, sm: 4 },

                display: "flex",
                flexDirection: "column",
                gap: 1,
                minWidth: "40%",
                transition: "transform .15s ease, box-shadow .15s ease",
                "&:hover": {
                    boxShadow: 3,
                },
            }}
        >
            {/* Title */}
            <Box
                sx={{
                    position: "relative",
                    pr: 4,
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 650,
                        lineHeight: 1.2,
                        display: "-webkit-box",
                        //WebkitLineClamp: 2,
                        //WebkitBoxOrient: "vertical",
                        //overflow: "hidden",
                    }}
                >
                    {item.title}
                </Typography>

                <IconButton
                    size="small"
                    component="a"
                    href={item.href}
                    rel="noreferrer"
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        color: `${articleAccentMap[item.index]} !important`,
                    }}
                >
                    <LaunchRoundedIcon fontSize="small" />
                </IconButton>
            </Box>


            {/* Description */}
            <Typography
                variant="body2"
                sx={{
                    color: "#585d68 !important",
                    display: "-webkit-box",
                    //WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    //overflow: "hidden",
                    fontWeight: 400,
                    lineHeight: 1.5,
                    fontFamily: "Lato, sans-serif",
                }}
            >
                {item.description}
            </Typography>

            {/* Meta + tags */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    mt: "auto",
                    gap: 0.75,
                }}
            >
                <Typography variant="caption" color="text.disabled">
                    {item.meta}
                </Typography>

                <Stack direction="row"
                       spacing={0.75}
                       useFlexGap
                       flexWrap="wrap">
                    {item.tags.map((t) => (
                        <Chip
                            key={t}
                            label={t}
                            size="small"
                            sx={{
                                borderRadius: 1,
                                height: 22,
                                fontSize: 12,
                                bgcolor: "grey.100",
                                fontWeight: 500,
                            }}
                        />
                    ))}
                </Stack>
            </Box>

        </Paper>
    );
}

export default function TechnicalWritingSection(): JSX.Element {
    return (
        <Box component="section" sx={{ px: gutterX, py: { xs: 6, md: 8, backgroundColor: "white" } }}>
            <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
                <Typography variant="h4" fontFamily="system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif;"
                            sx={{ fontWeight: 600 }}>
                    Technical{" "}
                    <Box component="span" sx={{ color: "#2766a8" }}>
                        Writing
                    </Box>
                </Typography>

                <Typography className="article-text"
                    variant="body1"
                    sx={{ mt: 1.5, color: "text.secondary", maxWidth: 780, mx: "auto" }}
                >
                    I write technical articles and guides for the developer community, spanning applied topics like distributed
                    systems and theoretical foundations such as information theory.
                </Typography>
            </Box>

            <Grid
                container
                rowSpacing={{ xs: 1.25, sm: 1.5, md: 2 }}
                columnSpacing={{ xs: 2, sm: 3, md: 4 }}
                >
                {articles.map((item) => (
                        <Grid key={item.title} item xs={12} md={4}>
                            <ArticleCard item={item} />
                        </Grid>
                ))}
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 6, md: 8 } }}>
                <Button
                    className="article-text"
                    variant="contained"
                    endIcon={<LaunchRoundedIcon />}
                    href="/blog"
                    sx={{
                        color: "#fff !important",//"#2766a8",
                        px: 3,
                        py: 2,
                        fontSize: '1rem !important',
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontFamily: "Lato, sans-serif",
                    }}
                    >
                    Read More Articles
                </Button>
            </Box>
        </Box>
);
}
